import './ProfileMenu.css';

import {Icon} from '@wandb/components';
import * as globals from '@wandb/weave/common/css/globals.styles';
import {useWindowSize} from '@wandb/weave/common/hooks/useWindowSize';
import {useBooleanState, useSyncedState} from '@wandb/weave/common/util/hooks';
import {isNotNullUndefinedOrFalse} from '@wandb/weave/common/util/types';
import {
  IconAddNew,
  IconAddToReport,
  IconChartVerticalBars,
  IconCodeAlt,
  IconCreditCardPayment,
  IconDashboardBlackboard,
  IconDocument,
  IconFlashBolt,
  IconImpersonateMaskAlt,
  IconJobCalculator,
  IconKeyAdmin,
  IconLightbulbInfo,
  IconLogOut,
  IconMagicWandStick,
  IconRandomizeAlt,
  IconSettings,
  IconSettingsParameters,
  IconShareWith,
  IconUserAuthor,
  IconUsersTeam,
} from '@wandb/weave/components/Icon';
import {TooltipDeprecated} from '@wandb/weave/components/TooltipDeprecated';
import React, {
  Fragment,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useHistory, useRouteMatch} from 'react-router-dom';

import config, {
  envIsPublicCloud,
  envIsServerDeployment,
  urlPrefixed,
} from '../../../config';
import {useAutoRefreshSettings} from '../../../containers/AutoRefreshSettings';
import {
  useProfileMenuOrgRoleInfoQuery,
  useServerInfoQuery,
  useViewerPhotoUrlQuery,
} from '../../../generated/graphql';
import {getOrganizationMemberRole} from '../../../pages/Billing/AccountSettings/util';
import {useIsOrgDashboardEnabled} from '../../../pages/OrgDashboard/useIsOrgDashboardEnabled';
import {UsageTabURLPart} from '../../../routes/usage/usagePaths';
import {auth} from '../../../setup';
import {useViewer} from '../../../state/viewer/hooks';
import {useAccountPrivileges} from '../../../util/accounts/pricing';
import {
  isInternalUser,
  setViewerUsingAdminPrivileges,
  useAdminModeActive,
  viewerUsingAdminPrivileges,
  viewingAs,
} from '../../../util/admin';
import {useForceTrace} from '../../../util/forceTrace';
import {
  useRampFlagAccountSelector,
  useRampFlagOrgAlertsTab,
  useRampFlagOrgLevelServiceAccounts,
  useRampFlagShowOrgActivityPage,
} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {
  accountSettingsActivityTab,
  accountSettingsAlertsTabWithSubtab,
  accountSettingsBillingTab,
  accountSettingsMembersTab,
  accountSettingsServiceAccountsTab,
  accountSettingsSettingsTab,
  accountSettingsTeamsTab,
  adminHome,
  adminUsers,
  homePage,
  orgDashboard,
  profilePage,
  settings,
  settingsApiKeys,
  subscriptions,
} from '../../../util/urls';
import {useColorMode} from '../../../util/useColorMode';
import {removeNullUndefinedOrFalse} from '../../../util/utility';
import {ImpersonateUserModal} from '../../ImpersonateUserModal';
import {LoginButton, SignupButton} from '../../LoginButton';
import {OrganizationMemberRole} from '../../MembersTable/util';
import {useShowJoinTeamsContent} from '../../ShowJoinTeamsContent';
import TeleportAccessRequestModal from '../../TeleportAccessRequestModal';
import UsageSimulationModal from '../../UsageSimulationModal';
import {ViewTeamsModal} from '../../ViewTeamsModal';
import {
  AccountSelectorContext,
  useAccountSelectorContext,
} from './AccountSelectorContextProvider';
import {AccountSelectorMenuItem} from './AccountSelectorMenuItem';
import {
  MenuDivider,
  MenuItem,
  MenuSection,
  MenuSectionHeader,
  type NavMenuItem,
  type NavMenuSection,
  PopupMenu,
  PopupMenuContent,
  PopupMenuShadow,
} from './NavMenu';
import * as S from './ProfileMenu.styles';
import {useProfileMenuContext} from './ProfileMenuContextProvider';
import {type Account, AccountType} from './types';
import {useAccountSelector} from './useAccountSelector';

const ProfileMenuComp = () => {
  const {
    state: menuOpen,
    setTrue: openMenu,
    setFalse: closeMenu,
  } = useBooleanState(false);

  const viewer = useViewer();
  const {selectedAccount} = useAccountSelectorContext();
  const enableAccountSelector = useRampFlagAccountSelector();
  const {isFullyConnected} = useProfileMenuContext();

  const history = useHistory();

  const {
    state: teleportAccessRequestModalOpen,
    setTrue: openTeleportAccessRequestModal,
    setFalse: closeTeleportAccessRequestModal,
  } = useBooleanState(false);
  const {
    state: limitConfigOpen,
    setTrue: openLimitConfig,
    setFalse: closeLimitConfig,
  } = useBooleanState(false);
  const {
    state: impersonateModalOpen,
    setTrue: openImpersonateModal,
    setFalse: closeImpersonateModal,
  } = useBooleanState(false);
  const [isViewTeamsModalOpen, setIsViewTeamsModalOpen] = useState(false);

  const profileMenuTrigger = useMemo(() => {
    if (viewer == null) {
      return null;
    }
    // Semantic UI Popup uses cloneElement to inject onClick/ref onto the
    // trigger. A wrapper div ensures those handlers reach a real DOM element
    // so ProfileMenuTrigger doesn't need forwardRef or rest-prop spreading.
    return (
      <div>
        <ProfileMenuTrigger
          viewerName={viewer.name}
          viewerUsername={viewer.username ?? ''}
          viewerPhotoURL={viewer.photoUrl}
        />
      </div>
    );
  }, [viewer]);

  if (viewer == null) {
    return (
      <div className="viewer-dropdown">
        <div className="logged-out-menu">
          <LoginButton />
          {!config.ENVIRONMENT_IS_PRIVATE && <SignupButton />}
        </div>
      </div>
    );
  }

  if (enableAccountSelector && !isFullyConnected && selectedAccount == null) {
    return null;
  }

  return (
    <>
      <div className="viewer-dropdown" data-test="profile-menu">
        <PopupMenu
          data-test="nav-profile-menu"
          on="click"
          basic
          position="bottom right"
          trigger={profileMenuTrigger}
          open={menuOpen}
          onOpen={openMenu}
          onClose={closeMenu}>
          {/* Perf: do not render the menu content and query data until the menu is open */}
          {menuOpen ? (
            <ProfileMenuContent
              closeMenu={closeMenu}
              openTeleportAccessRequestModal={openTeleportAccessRequestModal}
              openLimitConfig={openLimitConfig}
              openImpersonateModal={openImpersonateModal}
              setIsViewTeamsModalOpen={setIsViewTeamsModalOpen}
              isViewTeamsModalOpen={isViewTeamsModalOpen}
            />
          ) : null}
        </PopupMenu>
      </div>
      {impersonateModalOpen && (
        <ImpersonateUserModal
          onClose={closeImpersonateModal}
          onImpersonate={() => history.push(homePage())}
          onClick={e => {
            e.stopPropagation();
          }}
        />
      )}
      {limitConfigOpen && <UsageSimulationModal onClose={closeLimitConfig} />}
      {teleportAccessRequestModalOpen && (
        <TeleportAccessRequestModal onClose={closeTeleportAccessRequestModal} />
      )}
      {isViewTeamsModalOpen && (
        <ViewTeamsModal
          setIsModalOpen={setIsViewTeamsModalOpen}
          viewerEmail={viewer.email}
          location={'user menu'}
        />
      )}
    </>
  );
};

const ProfileMenuTrigger = ({
  viewerName,
  viewerUsername,
  viewerPhotoURL,
}: {
  viewerName: string;
  viewerUsername: string;
  viewerPhotoURL?: string;
}) => {
  const enableAccountSelector = useRampFlagAccountSelector();
  const {isFullyConnected} = useProfileMenuContext();

  const {data: viewerPhotoURLData} = useViewerPhotoUrlQuery({
    fetchPolicy: `network-only`,
  });
  const photoUrl = viewerPhotoURL || viewerPhotoURLData?.viewer?.photoUrl;

  if (!enableAccountSelector || isFullyConnected) {
    return (
      <S.Container data-test="nav-profile">
        <S.Avatar
          className="nav-propic"
          alt={`${viewerUsername}'s profile picture`}
          src={photoUrl}
          avatar
        />
      </S.Container>
    );
  }

  return (
    <Profile
      viewerName={viewerName}
      viewerUsername={viewerUsername}
      viewerPhotoURL={photoUrl ?? ''}
    />
  );
};

const Profile = ({
  viewerName,
  viewerUsername,
  viewerPhotoURL,
}: {
  viewerName: string;
  viewerUsername: string;
  viewerPhotoURL: string;
}) => {
  const {responsiveSize} = useWindowSize();
  const {isAdminModeActive, isImpersonating} = useProfileMenuContext();

  const {selectedAccount} = useAccountSelectorContext();
  const accountName = useMemo(() => {
    return selectedAccount?.accountType === AccountType.Personal &&
      selectedAccount?.isDummyAccount !== true
      ? 'Personal'
      : selectedAccount?.name;
  }, [selectedAccount]);

  const accountNameRef = useRef<HTMLDivElement>(null);

  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const element = accountNameRef.current;
    if (element) {
      const isOverflowing = element.scrollWidth > element.clientWidth;
      setShowTooltip(isOverflowing);
    }
  }, [accountName]);

  return (
    <S.ProfileButtonContainer
      data-test="nav-profile-account-selector"
      aria-label="Toggle profile & account menu">
      <S.ProfileButton>
        {responsiveSize !== 'small' && (
          <S.TextContainer>
            <S.UserName>{viewerName}</S.UserName>
            {showTooltip && (
              <TooltipDeprecated
                content={accountName}
                position="bottom center"
                trigger={
                  <S.AccountName
                    admin={isAdminModeActive}
                    ref={accountNameRef}
                    impersonation={isImpersonating}>
                    {accountName}
                  </S.AccountName>
                }
              />
            )}
            {!showTooltip && (
              <S.AccountName
                admin={isAdminModeActive}
                ref={accountNameRef}
                impersonation={isImpersonating}>
                {accountName}
              </S.AccountName>
            )}
          </S.TextContainer>
        )}
        <S.ProfileImage
          aria-label="Toggle profile & account menu"
          alt={`${viewerUsername}'s profile picture`}
          className="nav-propic"
          src={viewerPhotoURL}
        />
      </S.ProfileButton>
    </S.ProfileButtonContainer>
  );
};

type ProfileMenuContentProps = {
  closeMenu: () => void;
  openTeleportAccessRequestModal: () => void;
  openLimitConfig: () => void;
  openImpersonateModal: () => void;
  setIsViewTeamsModalOpen: (open: boolean) => void;
  isViewTeamsModalOpen: boolean;
};

const ProfileMenuContent = React.memo(
  ({
    closeMenu,
    openTeleportAccessRequestModal,
    openLimitConfig,
    openImpersonateModal,
    setIsViewTeamsModalOpen,
    isViewTeamsModalOpen,
  }: ProfileMenuContentProps) => {
    const {isDarkMode} = useColorMode();

    const {showTeamsContent: showJoinTeamsContent} =
      useShowJoinTeamsContent('Profile Menu');

    const sections: NavMenuSection[] = useProfileMenuSections({
      openTeleportAccessRequestModal,
      openLimitConfig,
      openImpersonateModal,
      setIsViewTeamsModalOpen,
      isViewTeamsModalOpen,
      showJoinTeamsContent,
    });

    return (
      <div>
        <PopupMenuContent>
          {sections.map(({items, header, backgroundColor}, sectionIndex) => (
            <Fragment key={`${sectionIndex}-${header}`}>
              {sectionIndex > 0 && <MenuDivider isNightMode={isDarkMode} />}
              <MenuSection backgroundColor={backgroundColor}>
                {header && <MenuSectionHeader>{header}</MenuSectionHeader>}
                {items.map((item, i) => (
                  <MenuItem
                    key={`${i}-${item.label}`}
                    item={item}
                    onClose={closeMenu}
                  />
                ))}
              </MenuSection>
            </Fragment>
          ))}
        </PopupMenuContent>
        {/* HAX: We want the menu to still be affected by the night mode filter,
      so we need a separate element for styles that should not be filtered like box-shadow */}
        <PopupMenuShadow isNightMode={isDarkMode} />
      </div>
    );
  }
);

const ProfileMenu = memo(ProfileMenuComp);
ProfileMenu.displayName = 'ProfileMenu';

export default ProfileMenu;

type UseProfileMenuSectionsParams = {
  openTeleportAccessRequestModal: () => void;
  openLimitConfig: () => void;
  openImpersonateModal: () => void;
  setIsViewTeamsModalOpen: (isViewTeamsModalOpen: boolean) => void;
  isViewTeamsModalOpen: boolean;
  showJoinTeamsContent: boolean;
};

const isOperatorEnabled = () =>
  (window.CONFIG as any)?.OPERATOR_ENABLED ?? false;

function getAccountSettingsTabs(
  selectedAccount: Account | null,
  canSeeBillingTab: boolean,
  canSeeServiceAccountsTab: boolean,
  canSeeActivityTab: boolean,
  showOrgAlertsTab: boolean,
  alertsSubtab?: string
): NavMenuItem[] {
  if (!envIsPublicCloud) {
    return [];
  }
  if (selectedAccount?.accountType === AccountType.Personal) {
    return [
      {
        icon: <IconKeyAdmin />,
        label: 'API keys',
        to: settingsApiKeys(),
      },
      {
        icon: <IconChartVerticalBars />,
        label: 'Usage',
        to: subscriptions(),
      },
    ];
  }
  return [
    {
      icon: <IconUserAuthor />,
      label: 'Users',
      to: accountSettingsMembersTab(selectedAccount?.name ?? ''),
    },
    {
      icon: <IconUsersTeam />,
      label: 'Teams',
      to: accountSettingsTeamsTab(selectedAccount?.name ?? ''),
    },
    canSeeActivityTab && {
      icon: <IconLightbulbInfo />,
      label: 'Activity',
      to: accountSettingsActivityTab(selectedAccount?.name ?? ''),
    },
    canSeeServiceAccountsTab && {
      icon: <IconUsersTeam />,
      label: 'Service Accounts',
      to: accountSettingsServiceAccountsTab(selectedAccount?.name ?? ''),
    },
    canSeeBillingTab && {
      icon: <IconCreditCardPayment />,
      label: 'Billing',
      to: accountSettingsBillingTab(selectedAccount?.name ?? ''),
    },
    showOrgAlertsTab && {
      icon: <Icon icon="scale-up" />,
      label: 'Usage & Alerts',
      to: accountSettingsAlertsTabWithSubtab(
        selectedAccount?.name ?? '',
        alertsSubtab ?? UsageTabURLPart.STORAGE
      ),
    },
    {
      icon: <IconKeyAdmin />,
      label: 'API keys',
      to: settingsApiKeys(),
    },
    {
      icon: <IconSettings />,
      label: 'Settings',
      to: accountSettingsSettingsTab(selectedAccount?.name ?? ''),
    },
    {
      icon: <IconAddNew />,
      label: 'Invite users',
      to: accountSettingsMembersTab(selectedAccount?.name ?? ''),
      variant: 'secondary',
    },
  ].filter(isNotNullUndefinedOrFalse);
}

function useProfileMenuSections({
  openTeleportAccessRequestModal,
  openLimitConfig,
  openImpersonateModal,
  setIsViewTeamsModalOpen,
  isViewTeamsModalOpen,
  showJoinTeamsContent,
}: UseProfileMenuSectionsParams): NavMenuSection[] {
  const {
    shouldShowAdminTools,
    teleportAdminCheckIsEnabled,
    isFullyConnected,
    isSignup,
  } = useProfileMenuContext();
  const viewer = useViewer();
  const inAdminMode = useAdminModeActive();

  const {isOrgDashboardEnabled} = useIsOrgDashboardEnabled();
  const alertsMatch = useRouteMatch<{subtab?: string}>(
    '/account-settings/:orgName/alerts/:subtab'
  );

  const {autoRefreshEnabled, setAutoRefreshEnabled} = useAutoRefreshSettings();
  const {state: autoRefreshEnabledLocal, setState: setAutoRefreshEnabledLocal} =
    useSyncedState({
      stateToSyncWith: autoRefreshEnabled,
      setStateToSyncWith: setAutoRefreshEnabled,
    });
  const toggleAutoRefresh = useCallback(() => {
    setAutoRefreshEnabledLocal(!autoRefreshEnabledLocal);
  }, [autoRefreshEnabledLocal, setAutoRefreshEnabledLocal]);

  const {
    state: viewerUsingAdminPrivilegesLocal,
    setState: setViewerUsingAdminPrivilegesLocal,
  } = useSyncedState({
    stateToSyncWith: viewerUsingAdminPrivileges(),
    setStateToSyncWith: setViewerUsingAdminPrivileges,
  });
  const toggleViewerUsingAdminPrivileges = useCallback(() => {
    setViewerUsingAdminPrivilegesLocal(!viewerUsingAdminPrivilegesLocal);
    setTimeout(() => window.location.reload(), 500);
  }, [viewerUsingAdminPrivilegesLocal, setViewerUsingAdminPrivilegesLocal]);

  const openJoinTeamsModal = useCallback(() => {
    setIsViewTeamsModalOpen(!isViewTeamsModalOpen);
  }, [isViewTeamsModalOpen, setIsViewTeamsModalOpen]);

  const serverInfoQuery = useServerInfoQuery();
  const isGoogleIap = serverInfoQuery.data?.serverInfo?.isGoogleIap;

  const canLogout = !config.AUTH_STUB_JWT || config.AUTH_STUB_JWT === 'false';
  const enableAccountSelector = useRampFlagAccountSelector();
  const {selectedAccount} = useContext(AccountSelectorContext);
  const accountSelector = useAccountSelector();
  const {data: orgRoleData} = useProfileMenuOrgRoleInfoQuery({
    variables: {
      orgName: selectedAccount?.name ?? '',
      username: viewer?.username ?? '',
    },
    skip:
      selectedAccount?.accountType === AccountType.Personal ||
      selectedAccount?.name == null ||
      viewer?.username == null,
  });
  const viewerMemberRole = getOrganizationMemberRole(
    orgRoleData?.organization?.member?.role ?? ''
  );
  const canSeeBillingTab =
    inAdminMode ||
    viewerMemberRole === OrganizationMemberRole.ADMIN ||
    viewerMemberRole === OrganizationMemberRole.BILLING_ONLY;
  const {data: canSeeServiceAccountsTab} = useRampFlagOrgLevelServiceAccounts(
    selectedAccount?.name ?? ''
  );
  const showOrgActivityPageFlag = useRampFlagShowOrgActivityPage();
  const showOrgAlertsTab =
    useRampFlagOrgAlertsTab() &&
    (viewerMemberRole === OrganizationMemberRole.ADMIN ||
      viewerMemberRole === OrganizationMemberRole.BILLING_ONLY);
  const {privileges} = useAccountPrivileges(selectedAccount);
  const canSeeActivityTab =
    showOrgActivityPageFlag && privileges.showMemberActivity;

  const {
    isActive: forceTraceActive,
    remainingLabel: forceTraceLabel,
    toggle: toggleForceTrace,
  } = useForceTrace();

  return useMemo(
    () =>
      removeNullUndefinedOrFalse([
        !isSignup && {
          header: viewer?.email,
          items: removeNullUndefinedOrFalse([
            {
              icon: <IconShareWith role="presentation" />,
              label: `Profile`,
              to: profilePage(viewer?.username ?? ``),
            },
            {
              icon: <IconSettingsParameters role="presentation" />,
              label: `User settings`,
              to: settings(),
            },

            isOrgDashboardEnabled && {
              icon: <IconDashboardBlackboard role="presentation" />,
              label: 'Organization dashboard',
              to: orgDashboard(),
            },
            {
              icon: (
                <TooltipDeprecated
                  content="Get updated in real-time without needing to reload the page."
                  className="tooltip"
                  basic
                  inverted
                  position="top left"
                  trigger={
                    <div className="tooltip-trigger">
                      <IconRandomizeAlt
                        className="refresh-icon"
                        role="presentation"
                      />
                      <span className="help-text underline-dashed">
                        Auto-refresh
                      </span>
                    </div>
                  }
                />
              ),
              label: '',
              onClick: toggleAutoRefresh,
              closeMenuOnClick: false,
              rightSide: (
                <S.Switch
                  checked={autoRefreshEnabledLocal}
                  data-test="auto-refresh-checkbox"
                />
              ),
              disableHover: true,
            },

            envIsPublicCloud &&
              !enableAccountSelector && {
                icon: <IconCreditCardPayment role="presentation" />,
                label: `Subscriptions`,
                to: subscriptions(),
              },

            showJoinTeamsContent && {
              icon: <IconUsersTeam role="presentation" />,
              label: `See new teams that you can join`,
              onClick: openJoinTeamsModal,
              variant: 'secondary',
            },
          ]),
        },

        enableAccountSelector &&
          !isFullyConnected && {
            header: `Account`,
            items: removeNullUndefinedOrFalse([
              envIsPublicCloud && {
                content: (
                  <AccountSelectorMenuItem
                    account={selectedAccount}
                    isSelectedAccount={true}
                  />
                ),
                variant: 'account-selector',
                disabled: true,
              },

              ...getAccountSettingsTabs(
                selectedAccount,
                canSeeBillingTab,
                canSeeServiceAccountsTab,
                canSeeActivityTab,
                showOrgAlertsTab,
                alertsMatch?.params.subtab
              ),
            ]),
          },

        !isSignup &&
          viewer?.admin && {
            header: `W&B admin tools`,
            backgroundColor: globals.MOON_100,
            items: removeNullUndefinedOrFalse([
              envIsPublicCloud &&
                shouldShowAdminTools && {
                  icon: <IconDocument role="presentation" />,
                  label: `Admin pages`,
                  to: adminHome(),
                },

              envIsPublicCloud &&
                teleportAdminCheckIsEnabled && {
                  icon: <IconAddToReport role="presentation" />,
                  label: `Request admin access`,
                  onClick: openTeleportAccessRequestModal,
                },

              !envIsServerDeployment &&
                shouldShowAdminTools && {
                  icon: <IconMagicWandStick role="presentation" />,
                  label: `Admin powers`,
                  onClick: toggleViewerUsingAdminPrivileges,
                  closeMenuOnClick: false,
                  rightSide: (
                    <S.Switch checked={viewerUsingAdminPrivilegesLocal} />
                  ),
                },

              inAdminMode &&
                !envIsServerDeployment && {
                  icon: <IconJobCalculator role="presentation" />,
                  label: `Simulate usage`,
                  onClick: openLimitConfig,
                },

              inAdminMode &&
                (!envIsServerDeployment || !!viewingAs()) &&
                shouldShowAdminTools && {
                  icon: <IconImpersonateMaskAlt role="presentation" />,
                  label: `Use as...`,
                  onClick: openImpersonateModal,
                },

              (!envIsServerDeployment || !!viewingAs()) &&
                window.location.host !== 'app.wandb.test' && {
                  icon: <IconCodeAlt role="presentation" />,
                  label: `Open in dev environment`,
                  onClick: () => {
                    // we can't use the history API since we need to change the origin
                    const redirect = `https://app.wandb.test${window.location.pathname}${window.location.search}`;
                    window.location.href = redirect; // eslint-disable-line
                  },
                },

              (!envIsServerDeployment || !!viewingAs()) &&
                window.location.host !== 'wandb.ai' && {
                  icon: <IconCodeAlt role="presentation" />,
                  label: `Open in prod environment`,
                  onClick: () => {
                    // we can't use the history API since we need to change the origin
                    const redirect = `https://wandb.ai${window.location.pathname}${window.location.search}`;
                    window.location.href = redirect; // eslint-disable-line
                  },
                },

              isInternalUser(viewer) && {
                icon: <IconFlashBolt role="presentation" />,
                label: forceTraceLabel
                  ? `Force traces (${forceTraceLabel})`
                  : `Force traces`,
                onClick: toggleForceTrace,
                closeMenuOnClick: false,
                rightSide: <S.Switch checked={forceTraceActive} />,
              },

              envIsServerDeployment &&
                !isOperatorEnabled() && {
                  icon: <IconSettingsParameters role="presentation" />,
                  label: `System settings`,
                  href: urlPrefixed(`/system-admin`, false),
                },

              envIsServerDeployment &&
                isOperatorEnabled() && {
                  icon: <IconSettingsParameters role="presentation" />,
                  label: `System console`,
                  href: urlPrefixed(`/console/settings`, false),
                },

              envIsServerDeployment &&
                !isOrgDashboardEnabled && {
                  icon: <IconShareWith role="presentation" />,
                  label: `Users`,
                  to: adminUsers(),
                },
            ]),
          },

        enableAccountSelector &&
          !isFullyConnected &&
          accountSelector.length > 0 && {
            header: `Other accounts`,
            items: removeNullUndefinedOrFalse(accountSelector),
          },

        canLogout && {
          items: removeNullUndefinedOrFalse([
            {
              icon: <IconLogOut role="presentation" />,
              label: `Log out`,
              onClick: () => auth.logout(),
              ...(isGoogleIap
                ? {
                    disabled: true,
                    tooltipText: `This instance is using Google IAP for authentication, so you'll need to log out of your Google account to log out of wandb.`,
                  }
                : {}),
            },
          ]),
        },
      ]),
    [
      isSignup,
      isFullyConnected,
      shouldShowAdminTools,
      teleportAdminCheckIsEnabled,
      isOrgDashboardEnabled,
      toggleAutoRefresh,
      autoRefreshEnabledLocal,
      showJoinTeamsContent,
      openJoinTeamsModal,
      toggleViewerUsingAdminPrivileges,
      viewerUsingAdminPrivilegesLocal,
      inAdminMode,
      openTeleportAccessRequestModal,
      openLimitConfig,
      openImpersonateModal,
      canLogout,
      isGoogleIap,
      accountSelector,
      enableAccountSelector,
      selectedAccount,
      canSeeBillingTab,
      canSeeServiceAccountsTab,
      canSeeActivityTab,
      showOrgAlertsTab,
      forceTraceActive,
      forceTraceLabel,
      toggleForceTrace,
      viewer,
      alertsMatch?.params.subtab,
    ]
  );
}
